import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import axios from 'axios';
import {
  PRODUCTS_INITIAL_STATE,
  ProductsActionParams,
  ProductsState
} from '@modules/products/store/products.model.ts';
import {
  createProduct,
  deleteProduct,
  editProduct,
  getInventoryCategories,
  togglePublish
} from '@api/products-api.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useProductsStore = create<ProductsState>()(
  devtools(
    (set, getState) => ({
      ...PRODUCTS_INITIAL_STATE,
      ...withCallState(getState),

      getInventoryCategories: async () => {
        try {
          const categoryData = await getInventoryCategories();

          const categoryList = categoryData.map((item) => ({
            id: item.id,
            departmentUnitId: item.departmentUnit?.id,
            title: item.name,
            value: item.name
          }));

          set({
            category: categoryList
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createProduct: async ({
        data,
        state,
        handleClose
      }: ProductsActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await createProduct(data as any);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_success_product_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      editProduct: async ({
        data,
        id,
        state,
        handleClose
      }: ProductsActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editProduct(data as any, id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_product_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteProduct: async ({
        id,
        state,
        handleClose
      }: ProductsActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await deleteProduct(id as number);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_delete_product_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      togglePublish: async ({
        id,
        state,
        handleClose
      }: ProductsActionParams) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const data = await togglePublish(id as number);

          const snackbarText = data?.isPublished
            ? 'snackbar_published_product_text'
            : 'snackbar_unpublished_product_text';

          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(snackbarText, SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      }
    }),
    { name: 'ProductsStore' }
  )
);

export default useProductsStore;
